import { openobserveRum } from '@openobserve/browser-rum';
import { openobserveLogs } from '@openobserve/browser-logs';
import { i18n } from '@/i18n';

/**
 * Enregistrement des sessions vers OpenObserve (auto-hébergé) : replay, clics, navigation,
 * erreurs JS, appels réseau et logs console (warn / error).
 *
 * Inactif tant que VUE_APP_OPENOBSERVE_CLIENT_TOKEN est vide.
 */

const CLIENT_TOKEN = process.env.VUE_APP_OPENOBSERVE_CLIENT_TOKEN || '';
// Hôte seul, sans protocole (ex. replay.shopimind.com). « localhost:5080 » bascule en http.
const SITE = process.env.VUE_APP_OPENOBSERVE_SITE || '';
const ORGANIZATION = process.env.VUE_APP_OPENOBSERVE_ORG || 'default';
const APPLICATION_ID = process.env.VUE_APP_OPENOBSERVE_APPLICATION_ID || 'frontv2';
const SERVICE = 'frontv2';

const EMAIL_PATTERN = /[^\s@<>"'(),;:]+@[^\s@<>"'(),;:]+\.[a-z]{2,}/gi;

let started = false;

function redactEmails(text: string): string {
  return text.replace(EMAIL_PATTERN, '[email]');
}

// Garde le nom des paramètres d'URL mais retire leur valeur (recherches, filtres, emails…).
function stripQueryValues(url: string): string {
  return url.replace(/([?&][^=&#]+)=[^&#]*/g, '$1=');
}

function normalizeText(text: string): string {
  return text.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}

function collectTranslations(node: unknown, into: Set<string>): void {
  if (typeof node === 'string') {
    // Les pluriels vue-i18n sont séparés par « | ».
    node.split('|').forEach((form) => into.add(normalizeText(form)));
  } else if (node && typeof node === 'object') {
    Object.values(node as Record<string, unknown>).forEach((child) => collectTranslations(child, into));
  }
}

/**
 * Mode « mask-unless-allowlisted » : dans les replays, tout texte affiché est remplacé par des
 * « x », sauf s'il figure dans cette liste. On y met les libellés de l'interface (fichiers de
 * traduction) : boutons, menus et titres restent lisibles, les données des marchands et de leurs
 * contacts (noms, emails, montants…) ne quittent jamais le navigateur.
 */
function installTextAllowlist(): void {
  const allowlist = new Set<string>();
  const { messages } = i18n.global as unknown as { messages: { value: Record<string, unknown> } };
  collectTranslations(messages.value, allowlist);

  // Le SDK compare le nœud texte brut ; Vue y laisse souvent des espaces autour du libellé.
  const nativeHas = allowlist.has.bind(allowlist);
  allowlist.has = (text: string) => nativeHas(normalizeText(text));

  (window as Window & { $DD_ALLOW?: Set<string> }).$DD_ALLOW = allowlist;
}

/**
 * Le suivi de session ne doit jamais casser frontv2 (serveur OpenObserve indisponible, SDK
 * défaillant…) : toute erreur levée par ce module est avalée ici, l'application continue.
 */
function safely(action: () => void): void {
  try {
    action();
  } catch (e) {
    console.warn('[SessionMonitoring]', e);
  }
}

interface ErrorFields {
  message?: string;
  stack?: string;
  causes?: ErrorFields[];
}

// Les beforeSend modifient l'événement reçu : c'est l'API prévue par le SDK pour expurger.
/* eslint-disable no-param-reassign */

// La pile d'appels recopie le message de l'erreur : elle doit être expurgée elle aussi.
function redactError(error: ErrorFields | undefined): void {
  if (!error) {
    return;
  }
  if (error.message) {
    error.message = redactEmails(error.message);
  }
  if (error.stack) {
    error.stack = redactEmails(error.stack);
  }
  if (error.causes) {
    error.causes.forEach(redactError);
  }
}

export function initSessionMonitoring(): void {
  if (started || !CLIENT_TOKEN || !SITE) {
    return;
  }
  started = true;

  safely(() => {
    installTextAllowlist();

    const common = {
      clientToken: CLIENT_TOKEN,
      site: SITE,
      organizationIdentifier: ORGANIZATION,
      insecureHTTP: SITE.startsWith('localhost'),
      service: SERVICE,
      env: window.location.hostname,
      version: process.env.VUE_APP_BUILD_SHA || 'local',
      sessionSampleRate: 100,
    };

    // Si l'expurgation échoue, l'événement est abandonné plutôt qu'envoyé en clair.
    openobserveRum.init({
      ...common,
      applicationId: APPLICATION_ID,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-unless-allowlisted',
      beforeSend: (event) => {
        try {
          event.view.url = stripQueryValues(event.view.url);
          if (event.view.referrer) {
            event.view.referrer = stripQueryValues(event.view.referrer);
          }
          if (event.type === 'resource') {
            event.resource.url = stripQueryValues(event.resource.url);
          }
          if (event.type === 'error') {
            redactError(event.error);
          }
          return true;
        } catch {
          return false;
        }
      },
    });

    openobserveLogs.init({
      ...common,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ['error', 'warn'],
      beforeSend: (event) => {
        try {
          event.message = redactEmails(event.message);
          redactError(event.error);
          if (event.view?.url) {
            event.view.url = stripQueryValues(event.view.url);
          }
          return true;
        } catch {
          return false;
        }
      },
    });
  });
}
/* eslint-enable no-param-reassign */

export interface SessionMonitoringUser {
  id: number | string;
  name: string;
  email: string;
}

export interface SessionMonitoringShop {
  id: number | null | undefined;
  solutionEcommerce?: string | null;
}

/** Rattache la session à l'utilisateur connecté et à la boutique active. */
export function identifySessionUser(user: SessionMonitoringUser, shop: SessionMonitoringShop): void {
  if (!started) {
    return;
  }
  safely(() => {
    const identity = { id: String(user.id), name: user.name, email: user.email };
    const shopContext = { id: shop.id ?? null, solution: shop.solutionEcommerce ?? null };

    openobserveRum.setUser(identity);
    openobserveRum.setGlobalContextProperty('shop', shopContext);
    openobserveLogs.setUser(identity);
    openobserveLogs.setGlobalContextProperty('shop', shopContext);
  });
}

/** À la déconnexion : oublie l'utilisateur et clôt la session (la suivante repart de zéro). */
export function clearSessionUser(): void {
  if (!started) {
    return;
  }
  safely(() => {
    openobserveRum.clearUser();
    openobserveRum.removeGlobalContextProperty('shop');
    openobserveLogs.clearUser();
    openobserveLogs.removeGlobalContextProperty('shop');
    openobserveRum.stopSession();
  });
}
